'use strict';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import store from 'store';
import AuthStore from '@stores/AuthStore';
import { fetchDocumentsById, updateCachedDocuments } from '@utils/Content';
import { fetchPlanPermissions } from '@utils/PlanPermissions';
import { getConfig } from '@utils/Env';
import Analytics from '@utils/Analytics';
import { fetchVirtualPlan } from '@utils/Plans';
import { getParticipantsForProfileByMealType } from '@utils/Meals';
import { getEditedCopy } from '../utils/localStorageManager';
import { useDietitianRecommendation } from './useDietitianRecommendation';
import confirm from '@utils/Confirm';

/**
 * Loading strategy types
 */
export const LOADING_STRATEGIES = {
    DATABASE: 'database',
    LOCAL_STORAGE: 'local_storage',
    VIRTUAL_PLAN: 'virtual_plan',
    PROMPT_USER: 'prompt_user'
};

/**
 * Ensure all meals in a plan have proper IDs
 * Generates IDs for meals that are missing them (legacy plans)
 * @param {Object} plan - Plan object with items array
 * @returns {Object} Plan with meals that have IDs
 */
const ensureMealIds = (plan) => {
    if (!plan || !plan.items || !Array.isArray(plan.items)) {
        return plan;
    }

    const itemsWithIds = plan.items.map(meal => {
        // If meal already has uuid or id, keep it as is
        if (meal.uuid || meal.id) {
            return meal;
        }

        // Generate a short ID (first 8 characters of UUID) for legacy meals
        const generatedId = uuid().substring(0, 8);

        return {
            ...meal,
            id: generatedId
        };
    });

    return {
        ...plan,
        items: itemsWithIds
    };
};

/**
 * Ensure all meals in a plan have participants
 * Computes missing participants based on meal type and profile settings
 * @param {Object} plan - Plan object with items array
 * @param {Object} profile - Profile object with preferences and family
 * @returns {Object} Plan with meals that have participants
 */
const ensureParticipants = (plan, profile) => {
    if (!plan || !plan.items || !Array.isArray(plan.items)) {
        return plan;
    }

    const itemsWithParticipants = plan.items.map(meal => {
        // If meal already has participants, keep it as is
        if (meal.participants && Array.isArray(meal.participants) && meal.participants.length > 0) {
            return meal;
        }

        // Compute participants based on meal type and profile settings
        let computedParticipants = [];

        if (profile && meal.meal) {
            // Get participants for this specific meal type from profile preferences
            const profileParticipants = getParticipantsForProfileByMealType(profile, meal.meal);

            // Convert participant objects to IDs
            computedParticipants = profileParticipants.map(participant => {
                // If it's the main profile, use 'USER' otherwise use the participant's UUID
                return (participant.uuid === profile.uuid) ? 'USER' : participant.uuid;
            });
        }

        // If we couldn't compute participants, default to ['USER']
        if (computedParticipants.length === 0) {
            computedParticipants = ['USER'];
        }

        return {
            ...meal,
            participants: computedParticipants
        };
    });

    return {
        ...plan,
        items: itemsWithParticipants
    };
};

/**
 * Load plan from database
 * @param {string} uuid - Plan UUID
 * @returns {Promise<Object>} Plan data with embedded assets
 */
const loadPlanFromDatabase = async (uuid) => {
    if (!uuid) {
        throw new Error('Plan UUID is required');
    }

    // Direct API fetch with assets embedded
    const response = await AuthStore.fetch(
        getConfig('recipe_api') + '/plans/' + uuid + '?embed=assets',
        null,
        true
    );

    if (response) {
        // Handle embedded assets
        if (response.assets) {
            updateCachedDocuments(response.assets);
            delete response.assets;
        }

        // Update cache with plan data
        updateCachedDocuments([response]);

        // Track analytics
        Analytics.customizePlan(response);

        // Ensure meals have IDs for proper meal management and participants
        const planWithIds = ensureMealIds(response);
        // Profile not available at load time, will be filled properly later
        return ensureParticipants(planWithIds, null);
    }

    throw new Error(`Failed to load plan ${uuid} from database`);
};

/**
 * Load plan from local storage (edited plan)
 * @param {string} uuid - Plan UUID
 * @returns {Object|null} Plan data from local storage
 */
const loadPlanFromLocalStorage = (uuid) => {
    if (!uuid) {
        return null;
    }

    const editedPlan = store.get('menu-edited-' + uuid);

    if (!editedPlan) {
        return null;
    }

    // Mark as dirty since it's from local storage and ensure meals have IDs and participants
    const planWithIds = ensureMealIds(editedPlan);
    // Profile not available at load time, will be filled properly later
    const planWithParticipants = ensureParticipants(planWithIds, null);
    return {
        ...planWithParticipants,
        _loadedFromLocalStorage: true
    };
};

/**
 * Hook for managing all plan loading concerns
 *
 * @param {string} planId - Plan UUID to load
 * @param {Object} virtualPlanHandler - Virtual plan handler from useVirtualPlan
 * @param {Object} initialPlan - Initial plan data (for testing)
 * @returns {Object} Loading state and functions
 */
export const usePlanLoading = (planId, virtualPlanHandler = null, initialPlan = null) => {
    // Local state tracking
    const [editedPlan, setEditedPlan] = useState(null);
    const [virtualPlan, setVirtualPlan] = useState(null);
    const [hasLocalChanges, setHasLocalChanges] = useState(false);
    const [isLocalStateReady, setIsLocalStateReady] = useState(false);

    // Loading state
    const [plan, setPlan] = useState(() => initialPlan || null);
    const [loading, setLoading] = useState(!!planId && !initialPlan);
    const [error, setError] = useState(null);
    const [shouldMarkDirty, setShouldMarkDirty] = useState(false);
    const [permissions, setPermissions] = useState({
        canWrite: false,
        canPublish: false,
        canCreate: false,
        canViewNutrition: true,
        canRecommend: false
    });

    // Dietitian recommendation checking
    const dietitianRec = useDietitianRecommendation(planId);


    /**
     * Check for edited plan in local storage
     */
    const checkEditedPlan = useCallback(() => {
        if (!planId) return null;

        const edited = getEditedCopy(planId);
        setEditedPlan(edited);
        return edited;
    }, [planId]);

    /**
     * Check for virtual plan
     */
    const checkVirtualPlan = useCallback(() => {
        if (!planId) return null;

        const virtual = fetchVirtualPlan(planId);
        setVirtualPlan(virtual);
        return virtual;
    }, [planId]);

    /**
     * Update local state
     */
    const updateLocalState = useCallback(() => {
        if (!planId) {
            setEditedPlan(null);
            setVirtualPlan(null);
            setHasLocalChanges(false);
            setIsLocalStateReady(true);
            return;
        }

        const edited = checkEditedPlan();
        const virtual = checkVirtualPlan();
        // Only treat as "local changes" if there's an edited plan - virtual plans are original state
        const hasChanges = !!edited;

        setHasLocalChanges(hasChanges);
        setIsLocalStateReady(true);
    }, [planId]); // Removed checkEditedPlan and checkVirtualPlan from dependencies

    // Update local state when planId changes
    useEffect(() => {
        updateLocalState();
    }, [planId]); // Only depend on planId, not the function itself

    /**
     * Determine the loading strategy
     */
    const strategy = useMemo(() => {
        if (!planId) {
            return LOADING_STRATEGIES.DATABASE;
        }

        // Rule 1: Dietitian-recommended plans ALWAYS load from database
        if (dietitianRec.isDietitianRecommended) {
            return LOADING_STRATEGIES.DATABASE;
        }

        // Rule 2: If we have local changes (for ANY plan type), prompt the user
        if (hasLocalChanges) {
            return LOADING_STRATEGIES.PROMPT_USER;
        }

        // Rule 3: Check if there's an unmodified virtual plan
        if (virtualPlanHandler?.virtualPlan) {
            return LOADING_STRATEGIES.VIRTUAL_PLAN;
        }

        // Rule 4: Default to loading from database
        return LOADING_STRATEGIES.DATABASE;
    }, [planId, dietitianRec.isDietitianRecommended, hasLocalChanges, virtualPlanHandler?.virtualPlan]);

    /**
     * Get the appropriate local plan based on priority
     */
    const getLocalPlan = useCallback(() => {
        // Edited plans take precedence over virtual plans
        if (editedPlan) {
            return {
                plan: editedPlan,
                source: LOADING_STRATEGIES.LOCAL_STORAGE
            };
        }

        if (virtualPlan) {
            return {
                plan: virtualPlan,
                source: LOADING_STRATEGIES.VIRTUAL_PLAN
            };
        }

        return null;
    }, [editedPlan, virtualPlan]);

    /**
     * Prompt user to restore changes or load fresh
     */
    const promptUserForRestoreChanges = useCallback(async () => {
        const localPlan = getLocalPlan();
        if (!localPlan) {
            // No local plan found, check for original virtual plan
            if (virtualPlanHandler?.virtualPlan) {
                return LOADING_STRATEGIES.VIRTUAL_PLAN;
            }
            return LOADING_STRATEGIES.DATABASE;
        }

        // Determine the original source (virtual or database)
        const hasVirtualPlan = virtualPlanHandler?.virtualPlan;
        const fallbackStrategy = hasVirtualPlan ? LOADING_STRATEGIES.VIRTUAL_PLAN : LOADING_STRATEGIES.DATABASE;

        const shouldRestore = await confirm(
            <div>
                <p>You have unsaved changes to this meal plan.</p>
                <p>Would you like to restore your changes, or load the original meal plan?</p>
            </div>,
            {
                acceptText: 'Restore Changes',
                rejectText: 'Load Original',
                titleText: 'Restore Changes?'
            }
        );

        if (shouldRestore) {
            return localPlan.source;
        } else {
            // User chose to discard changes - clear local storage
            clearLocalChanges();
            return fallbackStrategy;
        }
    }, [getLocalPlan, virtualPlanHandler?.virtualPlan]);

    /**
     * Clear local storage for this plan
     */
    const clearLocalStorage = useCallback(() => {
        if (!planId) return;

        store.remove('menu-edited-' + planId);
        setEditedPlan(null);
        setHasLocalChanges(false);
    }, [planId]);

    /**
     * Clear all local modifications
     */
    const clearLocalChanges = useCallback(() => {
        clearLocalStorage();
        setVirtualPlan(null);
    }, [clearLocalStorage]);

    /**
     * Execute the loading strategy and return the final decision
     */
    const executeStrategy = useCallback(async () => {
        switch (strategy) {
            case LOADING_STRATEGIES.DATABASE:
                return {
                    strategy: LOADING_STRATEGIES.DATABASE,
                    plan: null // Will be loaded from database
                };

            case LOADING_STRATEGIES.PROMPT_USER:
                const finalStrategy = await promptUserForRestoreChanges();

                if (finalStrategy === LOADING_STRATEGIES.DATABASE) {
                    return {
                        strategy: LOADING_STRATEGIES.DATABASE,
                        plan: null
                    };
                } else if (finalStrategy === LOADING_STRATEGIES.VIRTUAL_PLAN) {
                    return {
                        strategy: LOADING_STRATEGIES.VIRTUAL_PLAN,
                        plan: virtualPlanHandler?.virtualPlan || null
                    };
                } else if (finalStrategy === LOADING_STRATEGIES.LOCAL_STORAGE) {
                    const localPlan = getLocalPlan();
                    return {
                        strategy: LOADING_STRATEGIES.LOCAL_STORAGE,
                        plan: localPlan?.plan || null
                    };
                } else {
                    // Fallback case
                    const localPlan = getLocalPlan();
                    return {
                        strategy: finalStrategy,
                        plan: localPlan?.plan || null
                    };
                }

            case LOADING_STRATEGIES.VIRTUAL_PLAN:
                return {
                    strategy: LOADING_STRATEGIES.VIRTUAL_PLAN,
                    plan: virtualPlanHandler?.virtualPlan || null
                };

            default:
                // Fallback to database
                return {
                    strategy: LOADING_STRATEGIES.DATABASE,
                    plan: null
                };
        }
    }, [strategy, promptUserForRestoreChanges, getLocalPlan, virtualPlanHandler?.virtualPlan]);

    /**
     * Main loading function
     */
    const loadPlan = useCallback(async () => {
        if (!planId || initialPlan) {
            return;
        }

        // Don't reload if we already have the correct plan loaded
        if (plan && plan.uuid === planId) {
            return;
        }

        if (!isLocalStateReady) {
            return;
        }

        setLoading(true);
        setError(null);

        // Load permissions first
        if (fetchPlanPermissions) {
            try {
                const perms = await fetchPlanPermissions(planId);
                setPermissions(perms);
            } catch (err) {
                console.warn('Failed to load permissions, using defaults:', err);
                // Continue with default permissions - this is not a fatal error
            }
        }

        // Use loading strategy to determine how to load
        let loadingDecision;
        try {
            loadingDecision = await executeStrategy();
        } catch (err) {
            setError(err.message || 'Failed to determine loading strategy');
            setLoading(false);
            return;
        }

        let planData;
        let shouldMarkDirty = false;

        switch (loadingDecision.strategy) {
            case LOADING_STRATEGIES.LOCAL_STORAGE:
                // Profile not available at load time, will be filled properly later
                planData = ensureParticipants(ensureMealIds(loadingDecision.plan), null);
                shouldMarkDirty = true;
                break;

            case LOADING_STRATEGIES.VIRTUAL_PLAN:
                // Profile not available at load time, will be filled properly later
                planData = ensureParticipants(ensureMealIds(loadingDecision.plan), null);
                // Virtual plans don't need to be marked dirty initially
                break;

            case LOADING_STRATEGIES.DATABASE:
            default:
                try {
                    planData = await loadPlanFromDatabase(planId);
                } catch (err) {
                    setError(err.message || 'Plan not found');
                    setLoading(false);
                    return;
                }
                break;
        }

        if (planData) {
            setPlan(planData);
            setShouldMarkDirty(shouldMarkDirty);
        } else {
            setError('No plan data received');
            setLoading(false);
            return;
        }

        setLoading(false);
        return { plan: planData, shouldMarkDirty };
    }, [planId, initialPlan, isLocalStateReady, executeStrategy]);

    // Auto-load when ready
    useEffect(() => {
        loadPlan();
    }, [planId, isLocalStateReady]); // Check inside loadPlan prevents unnecessary reloads

    /**
     * Re-fetch permissions for a plan UUID
     * Useful when a virtual plan transitions to a real plan after saving
     */
    const refetchPermissions = useCallback(async (uuid) => {
        if (!fetchPlanPermissions || !uuid) {
            return;
        }

        try {
            const perms = await fetchPlanPermissions(uuid);
            setPermissions(perms);
        } catch (err) {
            console.warn('Failed to re-fetch permissions:', err);
        }
    }, []);

    return {
        // Plan data
        plan,

        // Loading state
        loading,
        error,
        permissions,

        // Loading strategy info
        strategy,
        isDietitianRecommended: dietitianRec.isDietitianRecommended,
        hasLocalChanges,
        boardContent: dietitianRec.boardContent,
        isReady: isLocalStateReady,
        shouldMarkDirty,

        // Actions
        loadPlan,
        setPlan,
        clearLocalChanges,
        refetchPermissions,

        // For external use
        STRATEGIES: LOADING_STRATEGIES
    };
};
